import { HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Severity, Status } from '@ui/aos-lib';
import { catchError, map, Observable, of } from 'rxjs';
import { DevicesPayload } from '../../../equipment/devices/components/devices.models';
import { EventConfigurationFormValue } from '../event-configuration-edit-window/event-configuration-form.type';
import { EventConfiguration, EventConfigurationTable } from '../event-configuration.types';
import { EventConfigurationApiService } from './event-configuration-api.service';
import {
  EventConfigurationActivityType,
  EventConfigurationAPIItem,
  EventConfigurationDeviceType,
  EventConfigurationOverrideAPIBody,
  EventConfigurationResponse,
  EventConfigurationSeverity,
  EventConfigurationSeverityRule,
  EventGroupsWithEventNamesResponse
} from './event-configuration-api.type';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { multiplierWithUom } from '../../../../shared/utils';

const severityRulesPresentationOrder: readonly EventConfigurationSeverity[] = [
  Severity.Critical,
  Severity.Severe,
  Severity.Warning,
  Severity.Notice
];

const deviceTypeNameMap: Readonly<Partial<Record<EventConfigurationDeviceType, string>>> = {
  [EventConfigurationDeviceType.All]: 'Any',
  [EventConfigurationDeviceType.BaseStation]: 'SBS',
  [EventConfigurationDeviceType.ExactaGuardGateway]: 'ExactaGuard',
  [EventConfigurationDeviceType.RMDSensor]: 'RMD'
} as const;

@Injectable({
  providedIn: 'root'
})
export class EventConfigurationService {
  private readonly eventConfigurationApiService = inject(EventConfigurationApiService);
  private readonly timezoneService = inject(TimezoneService);
  private readonly tableDataInitState: EventConfigurationTable = { data: [], total: 0 };

  prepareEventConfigOverrideBody(
    result: EventConfigurationFormValue,
    eventTypeId: number
  ): EventConfigurationOverrideAPIBody {
    const { description, enabledStatus: isEnabled } = result;
    // TODO: Specify the type. Now: `any[]`.
    let preparedSeverityRules = [];

    if (result.activityType === EventConfigurationActivityType.Alarm) {
      preparedSeverityRules = [{ severityLevel: result.severityLevel, threshold: null }];
    } else {
      preparedSeverityRules = Array.from(result.severityLevels.entries()).map(([severityLevel, threshold]) => ({
        severityLevel,
        threshold
      }));
    }

    const prepareEventConfigOverrideBody: EventConfigurationOverrideAPIBody = {
      description,
      isEnabled,
      eventTypeId,
      severityRules: preparedSeverityRules as EventConfigurationOverrideAPIBody['severityRules']
    };

    return prepareEventConfigOverrideBody;
  }

  getTableData(tableState: DevicesPayload): Observable<EventConfigurationTable> {
    return this.eventConfigurationApiService.getAll(tableState).pipe(
      map((eventConfiguration: EventConfigurationResponse): EventConfigurationTable => {
        if (!eventConfiguration) {
          return this.tableDataInitState;
        }

        const { items, totalCount: total } = eventConfiguration;
        const data = items.map((eventItem: EventConfigurationAPIItem): EventConfiguration => {
          const deviceTypeName = deviceTypeNameMap[eventItem.deviceType] ?? eventItem.deviceTypeName;
          const lastUpdateTimestamp = eventItem.lastUpdateTimestamp
            ? this.timezoneService.formatLong(eventItem.lastUpdateTimestamp)
            : eventItem.lastUpdateTimestamp;
          const severityRulesMap = new Map<EventConfigurationSeverity, EventConfigurationSeverityRule>();
          eventItem.severityRules.forEach(severityRule => {
            severityRulesMap.set(severityRule.severityLevel, severityRule);
          });
          const severityRules: EventConfigurationSeverityRule[] = [];
          severityRulesPresentationOrder.forEach(severityLevel => {
            const severityRule = severityRulesMap.get(severityLevel);
            if (severityRule) {
              severityRules.push(severityRule);
            }
          });
          const trigger: EventConfiguration['trigger'] = {
            activityType: eventItem.trigger.activityType,
            severityOperator: eventItem.severityOperator || eventItem.trigger.operator || '<'
          };

          return {
            description: eventItem.description ?? '',
            deviceType: eventItem.deviceType,
            deviceTypeName,
            commodity: eventItem.commodity,
            eventConfigurationStatus: eventItem.isEnabled ? Status.Active : Status.Inactive,
            eventGroup: eventItem.eventGroup,
            eventTypeId: eventItem.eventTypeId,
            isEnabled: eventItem.isEnabled,
            lastUpdateTimestamp,
            name: eventItem.name,
            severityRules,
            critical: severityRulesMap.get(Severity.Critical) || '',
            severe: severityRulesMap.get(Severity.Severe) || '',
            warning: severityRulesMap.get(Severity.Warning) || '',
            notice: severityRulesMap.get(Severity.Notice) || '',
            trigger,
            unitOfMeasure: multiplierWithUom(eventItem.multiplier, eventItem.unitOfMeasure),
            updatedBy: eventItem.updatedBy ?? ''
          };
        });

        return { data, total };
      }),
      catchError(() => of(this.tableDataInitState))
    );
  }

  overrideEventConfig(eventConfig: EventConfigurationOverrideAPIBody): Observable<HttpStatusCode> {
    return this.eventConfigurationApiService.overrideEventConfig(eventConfig);
  }

  getFilterEventNames(): Observable<EventGroupsWithEventNamesResponse[]> {
    return this.eventConfigurationApiService.getEventGroupsWithEventNames();
  }
}
